import { Widget } from '@automation-suite/shared-clients-widget-variant';
import { type MessageConsumer, type MessageProducer } from '@automation-suite/shared-clients-window-connection';
import { WidgetVariantFloatingActionCard } from '@automation-suite/shared-clients-widget-variant-fac';
import { logger } from '@automation-suite/shared-clients-widget-logger';

import { defaultIframeStyles, getNormalStyles, getFullscreenStyles } from './consts';
import {
  type Configs,
  type HostToIframeMessage,
  type IframeToHostMessage,
  type Options,
  type Actions,
  ActionMethod,
  type DisplayModes,
  type TransitionMap,
} from './types';
import { createTransitionMap, getDisplayModeToResponsiveStyles } from './utils';

import {
  ActionMethod as FacActionMethod,
  Options as FacOptions,
} from '@automation-suite/shared-clients-widget-variant-fac';

export class WidgetVariantEmbedded extends Widget<
  Actions,
  Configs,
  Options,
  MessageConsumer<IframeToHostMessage>,
  MessageProducer<HostToIframeMessage>
> {
  private intersectionObserver?: IntersectionObserver;
  private facWidget?: WidgetVariantFloatingActionCard;
  private processingQueue = false;
  private transitionQueue: (() => Promise<void>)[] = [];
  private transitionMap: TransitionMap | undefined;

  private get storageKeyDisplayMode(): string {
    return `${this.namespace}_display_mode`;
  }

  get currentDisplayMode(): DisplayModes {
    try {
      const savedMode = sessionStorage.getItem(this.storageKeyDisplayMode);
      if (savedMode === 'NORMAL' || savedMode === 'FULLSCREEN') {
        return savedMode;
      }
    } catch (error) {
      logger.warn(`Failed to read display mode from sessionStorage: ${error}`);
    }
    return 'NORMAL';
  }

  set currentDisplayMode(value: DisplayModes) {
    try {
      sessionStorage.setItem(this.storageKeyDisplayMode, value);
    } catch (error) {
      logger.warn(`Failed to save display mode to sessionStorage: ${error}`);
    }
  }

  public async destroy() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;

    await this.facWidget?.destroy();
    this.facWidget = undefined;

    this.processingQueue = false;
    this.transitionQueue = [];
    this.transitionMap = undefined;

    await super.destroy();
  }

  public getIframeStyles(isDesktop: boolean): Partial<CSSStyleDeclaration> {
    return isDesktop
      ? this.displayModeToResponsiveStyles[this.currentDisplayMode].desktop
      : this.displayModeToResponsiveStyles[this.currentDisplayMode].mobile;
  }

  public async beforeMount() {
    this.iframeToHostMessageConsumer?.addListener('transition-to-normal', () => this.transitionToDisplayMode('NORMAL'));
    this.iframeToHostMessageConsumer?.addListener('transition-to-fullscreen', () =>
      this.transitionToDisplayMode('FULLSCREEN')
    );
  }

  public async afterMount() {
    this.transitionMap = createTransitionMap({
      normalStyles: getNormalStyles(),
      fullscreenStyles: getFullscreenStyles(),
    });

    if (this.iframeElement) {
      const shouldRestoreFullscreen = this.currentDisplayMode === 'FULLSCREEN';

      this.applyStyles(
        this.isDesktopMediaQuery.matches
          ? this.displayModeToResponsiveStyles['NORMAL'].desktop
          : this.displayModeToResponsiveStyles['NORMAL'].mobile
      );

      if (shouldRestoreFullscreen) {
        this.hideIframe();
        this.currentDisplayMode = 'NORMAL';
        this.transitionToDisplayMode('FULLSCREEN');
      }
    }

    if (this.iframeElement && this.options?.['embedded'].dualMode) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hideFac();
            } else {
              this.showFac();
            }
          });
        },
        {
          threshold: 0,
          rootMargin: '0px',
        }
      );
      this.intersectionObserver.observe(this.iframeElement);
    }
  }

  private showFac() {
    if (!this.facWidget) {
      this.facWidget = new WidgetVariantFloatingActionCard({
        configs: this.configs,
        namespace: `${this.namespace}_embedded_fac`,
        analyticsDispatcher: this.analyticsDispatcher,
        loaderUrl: this.loaderUrl.href,
        optionOverrides: {
          ...(this.options as unknown as FacOptions),
          'floating-action-card': {
            ...((this.options as unknown as FacOptions)['floating-action-card'] ?? {}),
            fullScreenMode: true,
          },
        },
      });
      this.facWidget.init();
    } else {
      this.facWidget.execute(FacActionMethod.Show, { displayMode: 'FAC' });
    }
  }

  private hideFac() {
    if (this.facWidget?.currentDisplayMode !== 'WINDOW') {
      this.facWidget?.execute(FacActionMethod.Hide);
    }
  }

  private applyStyles(styles: Partial<CSSStyleDeclaration>) {
    if (this.iframeElement) {
      Object.assign(this.iframeElement.style, defaultIframeStyles);
      Object.assign(this.iframeElement.style, styles);
    }
  }

  private hideIframe() {
    if (this.iframeElement) {
      this.iframeElement.style.visibility = 'hidden';
    }
  }

  private showIframe() {
    if (this.iframeElement) {
      this.iframeElement.style.visibility = 'visible';
    }
  }

  private async asyncSendAndAwaitMessage(sendMessage: HostToIframeMessage, awaitMessage: IframeToHostMessage) {
    const listener = this.iframeToHostMessageConsumer?.addAsyncListener(awaitMessage.type);
    this.hostToIframeMessageProducer?.sendMessage(sendMessage);
    await listener;
  }

  private enqueueTransitionFunction(transitionFunction: () => Promise<void>) {
    this.transitionQueue.push(transitionFunction);
    if (!this.processingQueue) {
      this.startQueueProcessor();
    }
  }

  private async startQueueProcessor() {
    this.processingQueue = true;
    while (this.transitionQueue.length > 0) {
      const transitionFunction = this.transitionQueue.shift();
      if (transitionFunction) {
        await transitionFunction();
      }
    }
    this.processingQueue = false;
  }

  private async transitionToDisplayMode(toMode: DisplayModes) {
    this.enqueueTransitionFunction(async () => {
      if (this.currentDisplayMode === toMode) {
        return;
      }

      const shouldPreventScroll = toMode === 'FULLSCREEN';

      if (shouldPreventScroll) {
        this.preventParentScroll();
      } else {
        this.allowParentScroll();
      }

      if (!this.transitionMap) {
        const error = new Error('Transition map is not defined. Ensure the widget is initialized properly.');
        logger.error(error);
        throw error;
      }

      const toTransition = this.transitionMap[this.currentDisplayMode]?.[toMode];

      if (toTransition) {
        const startMessage = this.withFullscreenStartRect(toTransition.show.startTransitionMessageType);

        this.hideIframe();
        this.applyStyles(toTransition.styles(this.isDesktopMediaQuery.matches));
        await this.asyncSendAndAwaitMessage(startMessage, toTransition.show.completeTransitionMessageType);
        this.showIframe();

        this.currentDisplayMode = toMode;
      } else {
        logger.warn(`No transition defined from ${this.currentDisplayMode} to ${toMode}`);
      }
    });
  }

  private get displayModeToResponsiveStyles() {
    return getDisplayModeToResponsiveStyles();
  }

  private withFullscreenStartRect(message: HostToIframeMessage): HostToIframeMessage {
    if (message.type !== 'show-fullscreen' || !this.iframeElement) {
      return message;
    }

    const { top, left, width, height } = this.iframeElement.getBoundingClientRect();
    return {
      type: 'show-fullscreen',
      payload: { rect: { top, left, width, height } },
    };
  }

  protected actions: Actions = {
    ...this._actions,
    [ActionMethod.InitiateChat]: async ({ message, context } = {}) => {
      if (this.options?.['embedded'].fullScreenMode) {
        await this.transitionToDisplayMode('FULLSCREEN');
      }
      this.hostToIframeMessageProducer?.sendMessage({
        payload: { message, context },
        type: 'simplr-initiate-chat',
      });
    },
    [ActionMethod.InitiateSimplrChat]: async (params = {}) => {
      logger.warn(
        `'${ActionMethod.InitiateSimplrChat}' will be deprecated. Use "${ActionMethod.InitiateChat}" instead.`
      );
      this.actions[ActionMethod.InitiateChat](params);
    },
    [ActionMethod.ExtendContext]: (context) => {
      if (this.facWidget) {
        this.facWidget.execute(FacActionMethod.ExtendContext, context);
      }

      this._actions[ActionMethod.ExtendContext](context);
    },
    [ActionMethod.ExtendTrackingParameters]: (trackingParameters) => {
      if (this.facWidget) {
        this.facWidget.execute(FacActionMethod.ExtendTrackingParameters, trackingParameters);
      }

      this._actions[ActionMethod.ExtendTrackingParameters](trackingParameters);
    },
    [ActionMethod.SetLanguage]: (language) => {
      if (this.facWidget) {
        this.facWidget.execute(FacActionMethod.SetLanguage, language);
      }

      this._actions[ActionMethod.SetLanguage](language);
    },
  };

  protected getIframeUrl() {
    return process.env.NX_PUBLIC_WIDGET_USE_SAME_ORIGIN
      ? this.buildUrl(this.loaderUrl.origin, 'widget-variant-embedded')
      : this.buildUrl(process.env.NX_PUBLIC_EMBEDDED_IFRAME_ORIGIN as string);
  }
}
